import {
  Card,
  Flex,
  Select,
  MultiSelect,
  SelectOption,
  SimpleTable,
  Typography,
  Alert,
} from "djuno-design";
import { useState } from "react";
import ExpandableCard from "../../components/ExpandableCard";
import { GithubTypesLink } from "./../../urls";

const { Title, Text } = Typography;

const SelectPage = () => {
  const selectOptions: SelectOption<string>[] = [
    { label: "option 1", value: "option1" },
    { label: "option 2", value: "option2" },
  ];
  const [clearableValue, setClearableValue] = useState<string | undefined>(
    selectOptions[0].value
  );

  const [multiSelectValues, setMultiSelectValues] = useState<
    string[] | undefined
  >([]);

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Select</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The Select component in Djuno Design provides a flexible and
            customizable dropdown menu for selecting from a list of options.
            Designed to accommodate various data types and use cases, it
            supports features such as labeling, error handling, tooltips, hints,
            loading states, and optional features like clearable selections.
            With intuitive interaction patterns and a highly customizable API,
            the Select component is ideal for building rich, interactive forms
            or filtering interfaces.
          </Text>

          <Title level={5}>Variants and Customization</Title>
          <Text size="sm">
            The Select component offers several customization options:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Label and Required Field
                </Text>
                : You can attach a label to the dropdown, and mark it as
                required with a * indicator.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Error Handling
                </Text>
                : Displays error messages beneath the dropdown with smooth
                animations, signaling users about validation problems.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Hint and Tooltip
                </Text>
                : Add contextual hints or use tooltips to provide additional
                information without cluttering the interface.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Clearable
                </Text>
                : Allows users to clear the selected value, with a built-in
                clear button.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Loading States
                </Text>
                : When data is being fetched or processed, a spinner appears in
                the input field, indicating the loading state.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Size Variants
                </Text>
                : Supports different size options (small, medium, large) for
                various layout requirements.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Disabled State
                </Text>
                : Disable the entire component or specific options to prevent
                selection.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Empty State
                </Text>
                : Define a placeholder or custom empty string to guide users
                when no option is selected.
              </Text>
            </li>
          </ul>

          <Title level={5} className="mt-4">
            Additional Features
          </Title>
          <Text size="sm">
            The Select component offers additional features to enhance user
            experience:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Custom Options
                </Text>
                : Each option in the dropdown can include extra elements (e.g.,
                icons) or additional data, allowing for more complex selections.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Interactive Elements
                </Text>
                : The Select button supports interaction with other components
                such as icons or action buttons.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Clear Button
                </Text>
                : If a value is selected, the Select component can be made
                clearable, allowing users to easily remove their selection.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Floating and Responsive Dropdown
                </Text>
                : The dropdown positions itself dynamically using the floating
                UI, ensuring proper placement and responsiveness.
              </Text>
            </li>
          </ul>
        </Flex>

        {/* Examples */}
        <Flex direction="col" className="w-full">
          <Title level={5} className="mt-5">
            Examples
          </Title>
          <Flex direction="col" className="gap-4 w-full ">
            <ExpandableCard
              description="Select"
              className="gap-4 "
              codeViewerProps={{
                code: SelectExample1,
              }}
            >
              <Flex items="end" className="gap-3 w-full flex  mb-5">
                <Select
                  label="small"
                  uiSize="small"
                  options={selectOptions}
                  className="w-[200px]"
                  emptyString="select an option"
                />
                <Select
                  label="medium"
                  uiSize="medium"
                  options={selectOptions}
                  className="w-[200px]"
                />
                <Select
                  label="large"
                  uiSize="large"
                  options={selectOptions}
                  className="w-[200px]"
                />
              </Flex>
              <Flex items="end" className="gap-3 w-full flex  mb-5">
                <Select
                  label="loading"
                  loading
                  options={selectOptions}
                  className="w-[200px]"
                />
              </Flex>
              <Flex items="end" className="gap-3 w-full flex  mb-5">
                <Select
                  label="clear"
                  clearable
                  value={clearableValue}
                  options={selectOptions}
                  onChange={setClearableValue}
                  className="w-[200px]"
                />
              </Flex>
              <Flex items="end" className="gap-3 w-full flex  mb-5">
                <Select label="empty" options={[]} className="w-[200px]" />
              </Flex>
              <Flex items="end" className="gap-3 w-full flex  mb-5">
                <Select
                  label="tooltip"
                  tooltip={{ content: "This is a tooltip", clickable: true }}
                  options={[]}
                  className="w-[200px]"
                />
              </Flex>
              <Flex items="end" className="gap-3 w-full flex  mb-5">
                <Select
                  label="error"
                  error="Field is required"
                  required
                  options={[]}
                  className="w-[200px]"
                />
              </Flex>
              <Flex items="end" className="gap-3 w-full flex  mb-5">
                <Select
                  label="error"
                  error="Field is required"
                  required
                  options={[]}
                  className="w-[200px]"
                />
              </Flex>
              <Flex items="end" className="gap-3 w-full flex  mb-5">
                <MultiSelect
                  label="multi select"
                  clearable
                  values={multiSelectValues}
                  options={selectOptions}
                  onChange={(v) => {
                    console.log(v);
                    setMultiSelectValues(v);
                  }}
                  className="w-[200px]"
                />
              </Flex>
              <Flex items="end" className="gap-3 w-full flex  mb-5">
                <Select
                  label="Select with custom label"
                  uiSize="medium"
                  options={selectOptions}
                  className="w-[200px]"
                  emptyString="select an option"
                  labelClassName="text-green-500 font-bold"
                />
              </Flex>
            </ExpandableCard>
            <ExpandableCard
              description="MultiSelect"
              className="gap-4"
              codeViewerProps={{
                code: SelectExample2,
              }}
            >
              <Flex items="end" className="gap-3 w-full flex  mb-5">
                <MultiSelect
                  clearable
                  values={multiSelectValues}
                  options={selectOptions}
                  onChange={setMultiSelectValues}
                  className="w-[200px]"
                />
              </Flex>

              <Alert
                uiType="info"
                showIcon
                message={
                  <Text size="sm">
                    <Text size="sm" strong>
                      MultiSelect
                    </Text>{" "}
                    component is similar to{" "}
                    <Text size="sm" strong>
                      Select
                    </Text>{" "}
                    component but instead of{" "}
                    <Text size="sm" strong>
                      value
                    </Text>{" "}
                    you have{" "}
                    <Text size="sm" strong>
                      values
                    </Text>{" "}
                    (and also defaulValues). In addition you get an{" "}
                    <Text size="sm" strong>
                      array of strings
                    </Text>{" "}
                    in the onChange callback function
                  </Text>
                }
              />
            </ExpandableCard>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* id */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">id</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Unique identifier</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* label */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">label</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Text label displayed above the Select dropdown.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string | React.ReactNode
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* value */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">value</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The current selected value of the Select component.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`string | undefined`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* onChange */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">onChange</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Callback triggered when the selected value changes.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        function
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* options */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">options</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Array of options for the Select dropdown, each
                        containing a label, value, and optionally extra elements
                        or data.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        size="xs"
                        code
                        className="whitespace-nowrap"
                        target="_blank"
                        href={GithubTypesLink + "ISelect.ts#L27"}
                      >
                        {`SelectOption<ExtraDataType>[]`}
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* defaultValue */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">defaultValue</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Default value for the Select component, used for initial
                        state.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`string | undefined`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* loading */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">loading</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Displays a loading spinner when true, indicating that
                        the options are being fetched or processed.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* loadingType */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">loadingType</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Specifies the type of loading spinner to display.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Loading.ts#L23"}
                        size="xs"
                        code
                      >
                        LoadingType
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">simple</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* type */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">uiType</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Defines the visual style of the input field.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Input.ts#L44"}
                        size="xs"
                        code
                      >
                        InputTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">default</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* placeholder */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">placeholder</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Placeholder text shown inside the input when it is
                        empty.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* required */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">required</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Displays a * symbol next to the label to indicate the
                        Select is mandatory.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* error */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">error</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Error message displayed beneath the Select component
                        when validation fails.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`string | boolean | React.ReactNode`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* hint */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">hint</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Additional information displayed next to the label,
                        often used for guiding the user.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string | React.ReactNode
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* emptyString */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">emptyString</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Placeholder text displayed when no value is selected.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* tooltip */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">tooltip</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Tooltip configuration for providing more detailed
                        information about the Select.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Tooltip.ts#L37"}
                        size="xs"
                        code
                      >
                        TooltipProps
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* size */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">uiSize</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Defines the size of the Select component.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "index.ts#L55"}
                        size="xs"
                        code
                      >
                        SizeTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">medium</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
                {/* clearable */}
                <SimpleTable.Row>
                  <SimpleTable.TD>
                    <Text size="sm">clearable</Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="sm">
                      If true, allows users to clear the selected value.
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="xs" code>
                      {`boolean`}
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD></SimpleTable.TD>
                </SimpleTable.Row>
                {/* className */}
                <SimpleTable.Row>
                  <SimpleTable.TD>
                    <Text size="sm">className</Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="sm">
                      Additional CSS classes for customizing the overall
                      container of the Select component.
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="xs" code>
                      {`string`}
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD></SimpleTable.TD>
                </SimpleTable.Row>
                {/* buttonClassName */}
                <SimpleTable.Row>
                  <SimpleTable.TD>
                    <Text size="sm">buttonClassName</Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="sm">
                      Additional CSS classes for customizing the Select button.
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="xs" code>
                      {`string`}
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD></SimpleTable.TD>
                </SimpleTable.Row>
                {/* optionsClassName */}
                <SimpleTable.Row>
                  <SimpleTable.TD>
                    <Text size="sm">optionsClassName</Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="sm">
                      Additional CSS classes for customizing the dropdown
                      options container.
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="xs" code>
                      {`string`}
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD></SimpleTable.TD>
                </SimpleTable.Row>
                {/* optionClassName */}
                <SimpleTable.Row>
                  <SimpleTable.TD>
                    <Text size="sm">optionClassName</Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="sm">
                      Additional CSS classes for custom styling of the each
                      option.
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="xs" code>
                      {`string`}
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD></SimpleTable.TD>
                </SimpleTable.Row>
                {/* disabled */}
                <SimpleTable.Row>
                  <SimpleTable.TD>
                    <Text size="sm">disabled</Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="sm">
                      If true, disables the select component.
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="xs" code>
                      {`string`}
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD></SimpleTable.TD>
                </SimpleTable.Row>
                {/* labelClassName */}
                <SimpleTable.Row>
                  <SimpleTable.TD>
                    <Text size="sm">labelClassName</Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="sm">
                      Additional CSS classes to apply custom styles to the
                      input's label.
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="xs" code>
                      {`string`}
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD></SimpleTable.TD>
                </SimpleTable.Row>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const SelectExample1 = `<Flex items="end" className="gap-3 w-full flex ">
  <Select
    label="small"
    size="small"
    options={selectOptions}
    className="w-[200px]"
    emptyString="select an option"
  />
  <Select
    label="medium"
    size="medium"
    options={selectOptions}
    className="w-[200px]"
  />
  <Select
    label="large"
    size="large"
    options={selectOptions}
    className="w-[200px]"
  />
</Flex>
<Flex items="end" className="gap-3 w-full flex ">
  <Select
    label="loading"
    loading
    options={selectOptions}
    className="w-[200px]"
  />
</Flex>
<Flex items="end" className="gap-3 w-full flex ">
  <Select
    label="clear"
    clearable
    value={clearableValue}
    options={selectOptions}
    onChange={setClearableValue}
    className="w-[200px]"
  />
</Flex>
<Flex items="end" className="gap-3 w-full flex ">
  <Select label="empty" options={[]} className="w-[200px]" />
</Flex>
<Flex items="end" className="gap-3 w-full flex ">
  <Select
    label="tooltip"
    tooltip={{ content: "This is a tooltip", clickable: true }}
    options={[]}
    className="w-[200px]"
  />
</Flex>
<Flex items="end" className="gap-3 w-full flex ">
  <Select
    label="error"
    error="Field is required"
    required
    options={[]}
    className="w-[200px]"
  />
</Flex>
<Flex items="end" className="gap-3 w-full flex ">
  <Select
    label="error"
    error="Field is required"
    required
    options={[]}
    className="w-[200px]"
  />
</Flex>
<Flex items="end" className="gap-3 w-full flex ">
  <MultiSelect
    label="multi select"
    clearable
    values={multiSelectValues}
    options={selectOptions}
    onChange={(v) => {
      console.log(v);
      setMultiSelectValues(v);
    }}
    className="w-[200px]"
  />
</Flex>
<Flex items="end" className="gap-3 w-full flex ">
  <Select
    label="Select with custom label"
    size="medium"
    options={selectOptions}
    className="w-[200px]"
    emptyString="select an option"
    labelClassName="text-green-500 font-bold"
  />
</Flex>`;
const SelectExample2 = `<Flex items="end" className="gap-3 w-full flex ">
  <MultiSelect
    clearable
    values={multiSelectValues}
    options={selectOptions}
    onChange={setMultiSelectValues}
    className="w-[200px]"
  />
</Flex>

<Alert
  type="info"
  showIcon
  message={
    <Text size="sm">
      <Text size="sm" strong>
        MultiSelect
      </Text>
      component is similar to
      <Text size="sm" strong>
        Select
      </Text>
      component but instead of
      <Text size="sm" strong>
        value
      </Text>
      you have
      <Text size="sm" strong>
        values
      </Text>
      (and also defaulValues). In addition you get an
      <Text size="sm" strong>
        array of strings
      </Text>
      in the onChange callback function
    </Text>
  }
/>`;

export default SelectPage;
